import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

// Line graph card used by the e10/e11 projection screens (future-score symptom graph and
// the weight-loss goal graph). Geometry, colours and the line's vertices come from
// the Figma "symptom score graph" / "weight loss graph" components:
// https://www.figma.com/design/SHNQFk5bsHc6os6mZLhpTu/Web-quiz--app-?node-id=15383-166373
//
// The dot travels down the line, then the value bubble fades in above it.

const GraphCard = styled.div<{ $paddingY: number }>`
  background: #ffffff;
  border-radius: 16px;
  padding: ${({ $paddingY }) => $paddingY}px 20px;
`

const SVG = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
`

// Must match the @font-face family in src/styles/fonts.css: a display name with
// spaces only resolves for people who have the font installed locally.
const FONT = "'LotaGrotesqueAlt1', sans-serif"
const TEXT = '#4B4F60'
const TEXT_PRIMARY = '#2D3246'
const GRID = '#E2E2E2'

// Inner card width (335 − 2×20 padding) and the plot area inside it.
const WIDTH = 295
const TITLE_Y = 14 // baseline of the 14px title
const GRID_TOP = 42
const GRID_H = 119
const GRID_LEFT = 27
const GRID_RIGHT = 287
const X_LABELS_Y = GRID_TOP + GRID_H + 26
const HEIGHT = X_LABELS_Y + 4

const CARD_PADDING_X = 20 // matches GraphCard's horizontal padding

const DOT_R = 8
const POINTER_H = 8
const POINTER_W = 13
const BUBBLE_H = 28
const BUBBLE_GAP = 2 // between the pointer tip and the top of the dot

// Normalised [x, y] vertices of each design's line, measured from the Figma
// vectors (x: 0→1 across the plot, y: 0 = start value, 1 = end value).
export const SYMPTOM_LINE: [number, number][] = [
  [0, 0],
  [0.103, 0.057],
  [0.286, 0.342],
  [0.505, 0.55],
  [0.649, 0.763],
  [0.834, 0.868],
  [1, 1],
]
export const WEIGHT_LINE: [number, number][] = [
  [0, 0],
  [0.098, 0.054],
  [0.217, 0.306],
  [0.414, 0.48],
  [0.576, 0.757],
  [0.817, 0.868],
  [1, 1],
]

export type ProjectionGraphProps = {
  title: string
  // Values the line starts and ends at, in the same scale as yLabels.
  startValue: number
  endValue: number
  // Four gridline values, top to bottom. Omit to hide the axis entirely: the
  // gridlines stay evenly spaced and the line always runs the full height, so
  // even a small change still reads as a clear decline.
  yLabels?: number[]
  formatYLabel?: (v: number) => string
  bubbleText: string
  endDateLabel: string
  line: [number, number][]
  dotColor: string
  bubbleColor: string
  bubblePaddingX?: number
  cardPaddingY?: number
  durationMs?: number
}

export const ProjectionGraph: React.FC<ProjectionGraphProps> = ({
  title,
  startValue,
  endValue,
  yLabels,
  formatYLabel = (v) => String(Math.round(v)),
  bubbleText,
  endDateLabel,
  line,
  dotColor,
  bubbleColor,
  bubblePaddingX = 12,
  cardPaddingY = 12,
  durationMs = 2400,
}) => {
  const hasAxis = !!yLabels?.length
  const max = hasAxis ? yLabels[0] : startValue
  const min = hasAxis ? yLabels[yLabels.length - 1] : endValue
  const yFor = (v: number) => GRID_TOP + ((max - v) / (max - min || 1)) * GRID_H

  // Without an axis the plot starts at the left edge of the card.
  const gridLeft = hasAxis ? GRID_LEFT : 10
  const startY = GRID_TOP
  const endY = GRID_TOP + GRID_H
  const span = GRID_RIGHT - gridLeft
  const d = line
    .map(([t, f], i) => `${i === 0 ? 'M' : 'L'} ${gridLeft + span * t} ${startY + (endY - startY) * f}`)
    .join(' ')

  // The bubble is sized from the rendered text: a per-character estimate is out
  // by ~20px on a label like "11st 6lbs". The estimate is only the first-paint
  // value, before the measurement lands.
  const bubbleTextRef = useRef<SVGTextElement>(null)
  const [textW, setTextW] = useState<number | null>(null)
  const measure = () => {
    const w = bubbleTextRef.current?.getComputedTextLength?.()
    if (w) setTextW(w)
  }
  useLayoutEffect(measure, [bubbleText])
  // Re-measure once webfonts land, since widths change with the real font.
  useEffect(() => {
    document.fonts?.ready.then(measure)
  }, [bubbleText])

  // Bubble sits above the end dot, its pointer just clear of the dot. Short
  // labels ("75kg") may reach into the card's padding, as in the design; a long
  // one ("11st 6lbs") is nudged left so it stays inside the card, with the
  // pointer still anchored to the dot.
  const bubbleW = (textW ?? bubbleText.length * 9) + bubblePaddingX * 2
  const bubbleCx = Math.min(GRID_RIGHT, WIDTH + CARD_PADDING_X - 2 - bubbleW / 2)
  const pointerTipY = endY - DOT_R - BUBBLE_GAP
  const bubbleBottom = pointerTipY - POINTER_H
  const revealBegin = `${durationMs}ms`

  return (
    <GraphCard $paddingY={cardPaddingY}>
      <SVG viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label={`${title}: ${bubbleText}`}>
        <text
          x={WIDTH / 2}
          y={TITLE_Y}
          textAnchor="middle"
          fontFamily={FONT}
          fontSize="14"
          fontWeight="600"
          fill={TEXT}
        >
          {title}
        </text>

        {(hasAxis ? yLabels : [0, 1, 2, 3]).map((v, i) => {
          const y = hasAxis ? yFor(v) : GRID_TOP + (GRID_H / 3) * i
          return (
            <g key={v}>
              <line x1={gridLeft} x2={GRID_RIGHT} y1={y} y2={y} stroke={GRID} strokeWidth={1} />
              {hasAxis && (
                <text x={0} y={y + 5} fontFamily={FONT} fontSize="14" fill={TEXT}>
                  {formatYLabel(v)}
                </text>
              )}
            </g>
          )
        })}

        <path d={d} fill="none" stroke={TEXT_PRIMARY} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />

        {/* key={d} remounts the dot so the motion replays if the path changes. */}
        <circle key={d} r={DOT_R} fill={dotColor}>
          <animateMotion dur={revealBegin} fill="freeze" path={d} />
        </circle>

        <g opacity={0}>
          <animate attributeName="opacity" from="0" to="1" begin={revealBegin} dur="250ms" fill="freeze" />
          <rect
            x={bubbleCx - bubbleW / 2}
            y={bubbleBottom - BUBBLE_H}
            width={bubbleW}
            height={BUBBLE_H}
            rx={8}
            fill={bubbleColor}
          />
          <polygon
            points={`${GRID_RIGHT - POINTER_W / 2},${bubbleBottom} ${GRID_RIGHT + POINTER_W / 2},${bubbleBottom} ${GRID_RIGHT},${pointerTipY}`}
            fill={bubbleColor}
          />
          <text
            ref={bubbleTextRef}
            x={bubbleCx}
            y={bubbleBottom - BUBBLE_H / 2 + 5.5}
            textAnchor="middle"
            fontFamily={FONT}
            fontSize="16"
            fontWeight="600"
            fill={TEXT_PRIMARY}
          >
            {bubbleText}
          </text>
        </g>

        <text x={gridLeft} y={X_LABELS_Y} fontFamily={FONT} fontSize="14" fill={TEXT}>
          Today
        </text>
        <text x={GRID_RIGHT - 5} y={X_LABELS_Y} textAnchor="end" fontFamily={FONT} fontSize="14" fill={TEXT}>
          {endDateLabel}
        </text>
      </SVG>
    </GraphCard>
  )
}

// Symptom axis (Figma annotation): max = start rounded up to the nearest 10,
// min = end rounded down to the nearest 10, two evenly spaced lines between.
export const symptomAxis = (start: number, end: number): number[] => {
  let max = Math.ceil(start / 10) * 10
  const min = Math.max(0, Math.floor(end / 10) * 10)
  if (max <= min) max = min + 30
  const step = (max - min) / 3
  return [max, max - step, max - 2 * step, min]
}
